import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { WeatherService, WeatherConfig, DEFAULT_WEATHER_CONFIG } from '../../services/weather.service';
import { ToastrService } from 'ngx-toastr';

/**
 * Generische Wetter-Konfigurations-Komponente.
 *
 * Zeigt die Schwellwert-Konfiguration der Wetter-Interpretation an
 * und erlaubt deren Bearbeitung (wenn `canEdit = true`).
 *
 * Domänenspezifische Rohdaten-Reinterpretation wird über Callbacks gelöst:
 * - `rawData`: Beliebige Rohdaten (z.B. ein Dokument mit witterungRohdaten)
 * - `reinterpretFn`: Callback, das die Rohdaten mit der aktuellen Config neu interpretiert
 * - `reinterpreted`: Event, wenn die Reinterpretation ein Ergebnis liefert
 */
@Component({
	selector: 'wd-weather-config-modal',
	standalone: true,
	imports: [FormsModule],
	templateUrl: './weather-config-modal.component.html',
	styles: [`
		.help-text {
			color: #6c757d;
		}
		.accordion-button {
			padding: 0.5rem 1rem;
		}
		.accordion-body {
			padding: 0.75rem;
		}
		.form-label {
			font-size: 0.85rem;
			margin-bottom: 0.25rem;
		}
		.current-config table {
			font-size: 0.85rem;
		}
		.current-config td:first-child {
			white-space: nowrap;
			width: 120px;
		}
	`]
})
export class WdWeatherConfigModalComponent implements OnInit {
	private weatherService = inject(WeatherService);
	private toastr = inject(ToastrService);
	public activeModal = inject(NgbActiveModal);

	/** Ob der Benutzer die Schwellwerte bearbeiten darf */
	@Input() canEdit = false;

	/** Ob Rohdaten für Reinterpretation vorhanden sind */
	@Input() hasRawData = false;

	/** Beliebige Rohdaten für Reinterpretation */
	@Input() rawData?: any;

	/** Callback für domänenspezifische Reinterpretation der Rohdaten */
	@Input() reinterpretFn?: (rawData: any, config: WeatherConfig) => string;

	/** Emittiert das Ergebnis der Reinterpretation */
	@Output() reinterpreted = new EventEmitter<string>();

	config!: WeatherConfig;

	ngOnInit() {
		this.config = JSON.parse(JSON.stringify(this.weatherService.getConfig()));
	}

	reinterpret() {
		if (!this.rawData || !this.reinterpretFn) {
			this.toastr.warning('Keine Rohdaten vorhanden');
			return;
		}

		const result = this.reinterpretFn(this.rawData, this.config);
		this.reinterpreted.emit(result);
		this.toastr.success('Witterung neu interpretiert');
	}

	async save() {
		try {
			await this.weatherService.saveConfig(this.config);
			this.toastr.success('Wetter-Einstellungen gespeichert');
			this.activeModal.close();
		} catch (e) {
			this.toastr.error('Fehler beim Speichern');
		}
	}

	async resetToDefaults() {
		this.config = JSON.parse(JSON.stringify(DEFAULT_WEATHER_CONFIG));
		await this.weatherService.resetConfig();
		this.toastr.info('Auf Standardwerte zurückgesetzt');
	}
}
